import { useFetchMaterialsApi } from 'api/material'
import { useFetchTaskMasterSetsApi } from 'api/task_master_set'
import { CreateMaterialTaskForm, useCreateTasksFromTaskMasterSetApi } from 'api/task_set'
import { InputField, SelectField } from 'components/shared/input'
import { ModalButton } from 'components/shared/widget'
import { GlobalStateContext } from 'contexts/global_state_context'
import { MasterDataContext } from 'contexts/master_data_context'
import { MaterialSearchForm } from 'entities/material'
import { MaterialTaskData } from 'entities/task'
import { TaskMasterSetSearchForm } from 'entities/task_master_set'
import { useContext, useEffect } from 'react'
import { Button, CloseButton, OverlayTrigger, Table, Tooltip } from 'react-bootstrap'
import { Form, useEffectSkipFirst, useForm } from 'utils/hooks'

type Props = {
    createTasksForm: Form<CreateMaterialTaskForm>
    useTaskMaster?: boolean
    onComplete: () => void
}

export const CreateMaterialTaskModal = (props: Props) => {
    const { createTasksForm, onComplete, useTaskMaster } = props
    const globalState = useContext(GlobalStateContext)
    const masterData = useContext(MasterDataContext)
    const taskMasterSetSearchForm = useForm<TaskMasterSetSearchForm>({ kind: 'MATERIAL' })
    const taskMasterSetsApi = useFetchTaskMasterSetsApi(taskMasterSetSearchForm)
    const createTasksApi = useCreateTasksFromTaskMasterSetApi()
    const materialSearchForm = useForm<MaterialSearchForm>({ perPage: 100000 })
    const materialsApi = useFetchMaterialsApi(materialSearchForm)

    useEffect(() => {
        materialsApi.execute()
        taskMasterSetsApi.execute()
    }, [])

    useEffect(() => {
        const taskMasterSet = taskMasterSetsApi.response.results.find((tms) => tms.id === createTasksForm.object.taskMasterSetId)
        createTasksForm.update((f) => {
            f.taskDataList = taskMasterSet?.taskMasters?.map((tm) => {
                const data: MaterialTaskData = JSON.parse(tm.data!)
                return { taskMasterId: tm.id, number: data.number, materialName: data.material_name, materialId: data.material_id }
            })
        })
    }, [createTasksForm.object.taskMasterSetId])

    useEffectSkipFirst(() => {
        globalState.setLoading(createTasksApi.loading)
        if (createTasksApi.isSuccess()) {
            onComplete()
        }
    }, [createTasksApi.loading])
    return (
        <ModalButton
            modalProps={{ size: createTasksForm.object.institutionTasks ? 'xl' : 'lg' }}
            openButton={<Button variant="success">資材送付タスクを作成</Button>}
            modalHeader={<>{useTaskMaster ? 'タスクテンプレートから資材タスクを作成します' : '選択された組の資材タスクを作成します'}</>}
            modalBody={
                <div>
                    {createTasksForm.object.institutionTasks ? (
                        (() => {
                            const materials = [
                                ...(createTasksForm.object.taskDataList?.map((tdl) => tdl.materialName) || []),
                                ...(createTasksForm.object.materialTasks?.map((mt) => mt.materialInfo?.split(',')[1]) || []),
                                ...(createTasksForm.object.additionalTasks?.map((at) => at.materialName) || []),
                            ]
                            return (
                                <div style={{ overflow: 'scroll' }}>
                                    <Table>
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: 250, fontSize: 12 }}>医療機関</th>
                                                {materials.map((material, index) => {
                                                    return (
                                                        <th
                                                            style={{
                                                                fontSize: 12,
                                                                width: 100,
                                                                textAlign: 'center',
                                                            }}
                                                            key={'material-head-' + index}
                                                        >
                                                            <OverlayTrigger placement="top" overlay={<Tooltip id={`material-name`}>{material}</Tooltip>}>
                                                                <div style={{ width: 80, minWidth: 80 }}>
                                                                    {material?.substring(0, 5) + (material?.length ?? 0 > 5 ? '...' : '')}
                                                                </div>
                                                            </OverlayTrigger>
                                                        </th>
                                                    )
                                                })}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {createTasksForm.object.institutionTasks?.map((it, itIndex) => {
                                                return (
                                                    <tr key={'it-' + itIndex + `-${it.institutionId}`}>
                                                        <td style={{ verticalAlign: 'middle', fontSize: 12 }}>{it.institutionName}</td>
                                                        {materials.map((material, mIndex) => {
                                                            const materialIndex = it.taskInfo?.findIndex((ti) => ti.materialName === material) || 0
                                                            return (
                                                                <td
                                                                    key={`material-body-${itIndex}-${mIndex}-${it.institutionId}`}
                                                                    style={{ textAlign: 'center', verticalAlign: 'middle' }}
                                                                >
                                                                    {createTasksForm.getValue([
                                                                        'institutionTasks',
                                                                        itIndex,
                                                                        'taskInfo',
                                                                        materialIndex,
                                                                        'number',
                                                                    ]) ? (
                                                                        <InputField
                                                                            style={{ marginBottom: 0 }}
                                                                            form={createTasksForm}
                                                                            attr={['institutionTasks', itIndex, 'taskInfo', materialIndex, 'number']}
                                                                            type="number"
                                                                            min={1}
                                                                        />
                                                                    ) : (
                                                                        <div>-</div>
                                                                    )}
                                                                </td>
                                                            )
                                                        })}
                                                    </tr>
                                                )
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )
                        })()
                    ) : (
                        <div>
                            <SelectField
                                form={createTasksForm}
                                attr="userGroupId"
                                includeBlank
                                selectItems={masterData.userGroups.map((ug) => {
                                    return { label: ug.name, value: ug.id! }
                                })}
                                label="タスクの対象グループ"
                                required
                            />
                            {useTaskMaster && (
                                <>
                                    <SelectField
                                        form={createTasksForm}
                                        attr="taskMasterSetId"
                                        includeBlank
                                        selectItems={taskMasterSetsApi.response.results.map((tms) => {
                                            return { label: tms.title, value: tms.id! }
                                        })}
                                        label="タスクテンプレート"
                                        required
                                    />
                                    <Table style={{ verticalAlign: 'middle' }}>
                                        <thead>
                                            <tr>
                                                <th>資材</th>
                                                <th style={{ width: 80 }}>部数</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {createTasksForm.object.taskMasterSetId ? (
                                                (() => {
                                                    const taskMasterSet = taskMasterSetsApi.response.results.find(
                                                        (tms) => tms.id === createTasksForm.object.taskMasterSetId
                                                    )

                                                    return taskMasterSet?.taskMasters?.map((tm, index) => {
                                                        const data = JSON.parse(tm.data!)
                                                        return (
                                                            <tr key={'task-master-number-' + index}>
                                                                <td>{data.material_name}</td>
                                                                <td>
                                                                    <InputField
                                                                        style={{ marginBottom: 0 }}
                                                                        form={createTasksForm}
                                                                        attr={['taskDataList', index, 'number']}
                                                                        type={'number'}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        )
                                                    })
                                                })()
                                            ) : (
                                                <tr>
                                                    <td colSpan={2}>テンプレートを選択してください</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </>
                            )}

                            <div style={{ margin: '40px 0 40px 0', backgroundColor: '#f5f5f5', padding: 20 }}>
                                <div style={{ fontWeight: 'bold' }}>{useTaskMaster ? 'テンプレート以外の資材マスタを追加' : '選択された資材一覧'}</div>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th style={{ minWidth: 200 }}>資材</th>
                                            <th style={{ width: 80 }}>部数</th>
                                            <th style={{ width: 50 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {createTasksForm.object.materialTasks?.map((mt, index) => {
                                            return (
                                                <tr key={'material-tasks-' + index}>
                                                    <td>
                                                        <SelectField
                                                            form={createTasksForm}
                                                            attr={['materialTasks', index, 'materialInfo']}
                                                            style={{ marginBottom: 0 }}
                                                            selectItems={materialsApi.response.results.map((m) => {
                                                                return { label: m.name, value: `${m.id},${m.name}` }
                                                            })}
                                                            includeBlank
                                                        />
                                                    </td>
                                                    <td>
                                                        <InputField
                                                            form={createTasksForm}
                                                            attr={['materialTasks', index, 'number']}
                                                            style={{ marginBottom: 0 }}
                                                            type="number"
                                                            min={1}
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <CloseButton
                                                            onClick={() => {
                                                                createTasksForm.update((f) => {
                                                                    f.materialTasks?.splice(index, 1)
                                                                })
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                                {useTaskMaster && (
                                    <Button
                                        variant="secondary"
                                        style={{ width: '100%' }}
                                        onClick={() => {
                                            createTasksForm.update((f) => {
                                                if (!f.materialTasks) {
                                                    f.materialTasks = []
                                                }
                                                f.materialTasks?.push({})
                                            })
                                        }}
                                        disabled={
                                            createTasksForm.object.materialTasks &&
                                            createTasksForm.object.materialTasks.some((at) => !at.materialInfo && !at.number)
                                        }
                                    >
                                        追加
                                    </Button>
                                )}
                            </div>
                            <div style={{ margin: '40px 0 20px 0', backgroundColor: '#f5f5f5', padding: 20 }}>
                                <div style={{ fontWeight: 'bold' }}>資材マスタに存在しない資材（自由入力）</div>
                                <Table>
                                    <thead>
                                        <tr>
                                            <th style={{ width: 200 }}>資材</th>
                                            <th>URL</th>
                                            <th style={{ width: 80 }}>部数</th>
                                            <th style={{ width: 50 }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {createTasksForm.object.additionalTasks?.map((at, index) => {
                                            return (
                                                <tr key={'additional-' + index}>
                                                    <td>
                                                        <InputField
                                                            form={createTasksForm}
                                                            attr={['additionalTasks', index, 'materialName']}
                                                            style={{ marginBottom: 0 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <InputField
                                                            form={createTasksForm}
                                                            attr={['additionalTasks', index, 'url']}
                                                            style={{ marginBottom: 0 }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <InputField
                                                            form={createTasksForm}
                                                            attr={['additionalTasks', index, 'number']}
                                                            style={{ marginBottom: 0 }}
                                                            type="number"
                                                            min={1}
                                                        />
                                                    </td>
                                                    <td style={{ verticalAlign: 'middle' }}>
                                                        <CloseButton
                                                            onClick={() => {
                                                                createTasksForm.update((f) => {
                                                                    f.additionalTasks?.splice(index, 1)
                                                                })
                                                            }}
                                                        />
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </Table>
                                <Button
                                    variant="secondary"
                                    style={{ width: '100%' }}
                                    onClick={() => {
                                        createTasksForm.update((f) => {
                                            if (!f.additionalTasks) {
                                                f.additionalTasks = []
                                            }
                                            f.additionalTasks?.push({})
                                        })
                                    }}
                                    disabled={
                                        createTasksForm.object.additionalTasks &&
                                        createTasksForm.object.additionalTasks.some((at) => !at.materialName && !at.number)
                                    }
                                >
                                    追加
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            }
            footerButton={
                <>
                    {createTasksForm.object.institutionTasks && (
                        <Button
                            variant="secondary"
                            onClick={() => {
                                createTasksForm.update((f) => delete f.institutionTasks)
                            }}
                        >
                            戻る
                        </Button>
                    )}
                    {!createTasksForm.object.institutionTasks && (
                        <Button
                            disabled={(useTaskMaster && !createTasksForm.object.taskMasterSetId) || !createTasksForm.object.userGroupId}
                            variant="primary"
                            onClick={() => {
                                createTasksForm.update((f) => {
                                    f.institutionTasks = createTasksForm.object.institutions?.map((institution) => {
                                        return {
                                            institutionId: institution.id,
                                            institutionName: institution.name,
                                            taskInfo: [
                                                ...[
                                                    ...(createTasksForm.object.taskDataList?.map((tdl) => {
                                                        return {
                                                            materialId: tdl.materialId,
                                                            materialName: tdl.materialName,
                                                            taskMasterId: tdl.taskMasterId,
                                                            number: tdl.number,
                                                        }
                                                    }) || []),
                                                    ...(createTasksForm.object.materialTasks?.map((mt) => {
                                                        const [id, name] = mt.materialInfo?.split(',') || []
                                                        return { materialId: id, materialName: name, number: mt.number }
                                                    }) || []),
                                                ].filter((i) => {
                                                    if (createTasksForm.object.institutionMaterials) {
                                                        return createTasksForm.object.institutionMaterials[institution.id!].includes(i.materialName!)
                                                    } else {
                                                        return true
                                                    }
                                                }),
                                                ...(createTasksForm.object.additionalTasks?.map((at) => {
                                                    return { materialName: at.materialName, url: at.url, number: at.number }
                                                }) || []),
                                            ],
                                        }
                                    })
                                })
                                // createTasksApi.execute(createTasksForm)
                            }}
                        >
                            最終確認
                        </Button>
                    )}
                </>
            }
            submitButton={
                <>
                    {createTasksForm.object.institutionTasks && (
                        <Button
                            variant="success"
                            onClick={() => {
                                createTasksApi.execute(createTasksForm)
                            }}
                        >
                            タスク作成
                        </Button>
                    )}
                </>
            }
        />
    )
}
